import {
  Avatar,
  Box,
  Button,
  ButtonGroup,
  Center,
  Collapse,
  Editable,
  EditableInput,
  EditablePreview,
  Flex,
  IconButton,
  Spinner,
  Text,
  Textarea as ChakraTextarea,
  Tooltip,
  useClipboard,
  useEditableControls,
  useToast,
} from '@chakra-ui/react';
import { conversationAction } from 'actions';
import MarkdownRenderer from 'components/MarkdownRenderer';
import { TEXT_CONSTANT } from 'helpers/constant';
import { timeFormat } from 'helpers/date';
import { CONVERSATION_STATUS } from 'helpers/enum';
import { exportAnswerToWord } from 'helpers/exportToWord';
import { Check, ChevronDown, ChevronUp, Copy, FileDown, FileText, Pencil } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Model } from 'services/model/model.type';
import { User } from 'types';
import { Citation, Message } from 'types/reducers/conversation';

import TypingText from './TypingText';

const CITATIONS_EXPAND_HEIGHT_OFFSET = 100;

interface EditableTextareaProps {
  value: string;
  onChange: (value: string) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
}

const EditableTextarea = ({ value, onChange: onChangeProp, onKeyDown }: EditableTextareaProps) => {
  const { isEditing } = useEditableControls();
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);

  const adjustHeight = React.useCallback(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = '0px';
      const scrollHeight = textarea.scrollHeight;
      const lineHeight = parseInt(getComputedStyle(textarea).lineHeight) || 20;
      const minHeight = lineHeight + 10;
      textarea.style.height = `${Math.max(minHeight, Math.min(scrollHeight, 500))}px`;
    }
  }, []);

  React.useEffect(() => {
    if (isEditing) {
      const timer = setTimeout(() => {
        adjustHeight();
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [isEditing, adjustHeight]);

  React.useEffect(() => {
    if (isEditing) {
      const timer = setTimeout(adjustHeight, 0);
      return () => clearTimeout(timer);
    }
  }, [value, isEditing, adjustHeight]);

  if (!isEditing) {
    return null;
  }

  const lineCount = value ? value.split('\n').length : 1;
  const estimatedHeight = Math.max(40, lineCount * 20 + 10);

  return (
    <ChakraTextarea
      ref={textareaRef}
      sx={{
        height: 'auto',
        minHeight: `${estimatedHeight}px`,
        lineHeight: '1.4',
      }}
      pos='relative'
      zIndex={1}
      overflow='hidden'
      w='100%'
      minH={`${estimatedHeight}px`}
      px={0}
      bg='inherit'
      _focus={{ outline: 'none', boxShadow: 'none' }}
      whiteSpace='pre-line'
      resize='none'
      onChange={(e) => {
        onChangeProp(e.target.value);
        setTimeout(adjustHeight, 0);
      }}
      onKeyDown={onKeyDown}
      value={value || ''}
    />
  );
};

interface MessageChatProps {
  data: Message;
  onOpenFile: (_citation: Citation) => void;
  status: CONVERSATION_STATUS;
  isLast: boolean;
  user: User;
  cancelEditRef: React.RefObject<HTMLButtonElement>;
}

const MessageChat = ({
  data,
  onOpenFile,
  status,
  isLast,
  user,
  cancelEditRef,
}: MessageChatProps) => {
  const { t } = useTranslation();
  const toast = useToast();
  const citationsRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();
  const {
    current,
    processingStreamingChunkStatus,
    status: conversationStatus,
  } = useSelector((store) => store.conversations);

  const [isHovering, setIsHovering] = useState(false);
  const [isShowFullCitation, setIsShowFullCitation] = useState(false);
  const [isShowViewMoreButton, setIsShowViewMoreButton] = useState(false);
  const [question, setQuestion] = useState('');
  const models = useSelector((state: any) => state.model?.models || []);
  const [displayModelName, setDisplayModelName] = useState<string | null>(null);

  const isError = data?.status === 'error';
  const isLastMessageWithoutAnswer = Boolean(isLast && !data.answer && data.status !== 'error');
  const isShowEditIcon = Boolean(
    isHovering && isLast && status !== CONVERSATION_STATUS.GENERATE_ANSWER && data.answer
  );
  const hasCitation = data?.citations && data.citations?.length > 0;

  const lastAnswerResult = data.answer ? (
    <MarkdownRenderer content={data.answer} />
  ) : !isError &&
    (conversationStatus === CONVERSATION_STATUS.LOADING ||
      conversationStatus === CONVERSATION_STATUS.GENERATE_ANSWER ||
      processingStreamingChunkStatus ||
      data.status === 'loading' ||
      isLastMessageWithoutAnswer ||
      !data.status) ? (
    <Box pt={2}>
      <Spinner color='gray.400' size='sm' />
    </Box>
  ) : (
    <div>{TEXT_CONSTANT.CONVERSATION.ERROR_ANSWER}</div>
  );

  const onMouseEnter = () => {
    setIsHovering(true);
  };

  const onMouseLeave = () => {
    setIsHovering(false);
  };

  const onToggleShowFullCitation = () => setIsShowFullCitation(!isShowFullCitation);

  // Copy the answer to the clipboard (mockup parity: copy icon next to Export to Word).
  const { hasCopied, onCopy: onCopyAnswer } = useClipboard(data?.answer ?? '');

  const onExportToWord = () => {
    exportAnswerToWord({
      content: data.answer ?? '',
      title: data.question,
      fileName: data.question,
    }).catch(() => {
      toast({
        title: t('common.exportWordError', { defaultValue: 'Failed to export to Word' }),
        status: 'error',
        position: 'bottom-right',
        isClosable: true,
      });
    });
  };

  const handleKeyDownEditable = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !question) {
      e.preventDefault();
      return false;
    }
  };

  const onSubmidEditQuestion = (question: string) => {
    if (question) {
      dispatch(conversationAction.editQuestion({ question, created_at: new Date().toISOString() }));
      dispatch(conversationAction.updateProcessingStreamingChunkStatus(true));
      dispatch(conversationAction.setTimeOutForFinalAnswer(true));
    } else {
      toast({
        title: `Question must not empty`,
        status: 'warning',
        position: 'bottom-right',
        isClosable: true,
      });
    }
  };

  useEffect(() => {
    const citationsHeight = citationsRef?.current?.clientHeight ?? 0;
    const isCitationsCollapsable = citationsHeight > CITATIONS_EXPAND_HEIGHT_OFFSET;

    setIsShowViewMoreButton(isCitationsCollapsable);
    setIsShowFullCitation(!isCitationsCollapsable);
  }, [data?.citations]);

  useEffect(() => {
    setQuestion(data?.question);
  }, [data?.question]);

  useEffect(() => {
    if (data?.requested_model && models.length > 0) {
      // Use requested_model from API response if available
      const model = models.find(
        (m: Model) =>
          m.model_name === data.requested_model ||
          m.name === data.requested_model ||
          m.id === data.requested_model
      );
      if (model) {
        setDisplayModelName(model.display_name || model.name);
      } else {
        // Fallback to requested_model if not found in models
        setDisplayModelName(data.requested_model);
      }
    } else if (data?.model?.name) {
      // Fallback to old model.name if requested_model not available
      const model = models.find(
        (m: Model) => m.name === data.model?.name || m.id === data.model?.name
      );
      setDisplayModelName(model?.display_name || data.model.name);
    } else if (!data?.answer && current?.selectedModelId && models.length > 0) {
      // When waiting for response, use selectedModelId from current conversation
      const model = models.find((m: Model) => m.id === current.selectedModelId);
      if (model) {
        setDisplayModelName(model.display_name || model.name);
      }
    } else {
      setDisplayModelName(null);
    }
  }, [data?.requested_model, data?.model, data?.answer, current?.selectedModelId, models]);

  useEffect(() => {
    if (data.answer) {
      dispatch(conversationAction.updateProcessingStreamingChunkStatus(false));
      dispatch(conversationAction.setTimeOutForFinalAnswer(false));
      dispatch(conversationAction.clearStreamingAnswerChunks());
    }
  }, [data.answer]);

  return (
    <>
      {/* User question — DS primary bubble, right-aligned */}
      <Flex align='flex-start' justify='flex-end' gap={3} pt={{ base: 5 }}>
        <Box pos='relative' maxW={{ base: '80%', md: '70%' }} fontSize='base'>
          <Editable
            key={data?.question}
            isPreviewFocusable={false}
            onCancel={() => {
              setQuestion(data?.question || '');
            }}
            onChange={(value) => setQuestion(value)}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            onSubmit={onSubmidEditQuestion}
            selectAllOnFocus={false}
            submitOnBlur={false}
            value={question}
          >
            <Box
              pos='relative'
              mb={1}
              px={5}
              py={3}
              pe={10}
              color='primary.on'
              lineHeight={1.6}
              bg='primary.solid'
              opacity={isError ? 0.5 : 1}
              borderRadius='14px 14px 4px 14px'
              shadow='ds_xs'
              whiteSpace='pre-line'
            >
              {isLast ? (
                <>
                  <EditablePreview maxW='100%' whiteSpace='pre-line' />
                  <Box
                    as={EditableInput}
                    sx={{
                      display: 'none !important',
                      visibility: 'hidden !important',
                      position: 'absolute',
                      opacity: 0,
                      pointerEvents: 'none',
                    }}
                  />
                  <EditableTextarea
                    value={question}
                    onChange={setQuestion}
                    onKeyDown={handleKeyDownEditable}
                  />
                </>
              ) : (
                <>{data?.question}</>
              )}
            </Box>
            <Flex align='center' justify='flex-end' gap={2} color='text.subtle' fontSize='xs'>
              <EditableControls
                isShowEditIcon={isShowEditIcon}
                cancelRef={cancelEditRef}
                value={question}
              />
              {isError && (
                <Text as='span' color='state.error'>
                  Error sent
                </Text>
              )}
              {timeFormat(data?.created_at)}
            </Flex>
          </Editable>
        </Box>
        <Avatar
          color='primary.on'
          bg='primary.solid'
          name={user.name?.replace(/\s+/g, ' ')}
          size='sm'
        />
      </Flex>
      {/* Assistant answer — branded mark + DS surface card */}
      <Flex align='flex-start' justify='flex-start' gap={3} overflowX='hidden' w='100%' mt={3}>
        {!isError ? (
          <>
            <Flex
              align='center'
              justify='center'
              flexShrink={0}
              w='32px'
              h='32px'
              color='primary.on'
              fontFamily='heading'
              fontWeight={700}
              bg='primary.solid'
              borderRadius='md'
              shadow='ds_xs'
            >
              A
            </Flex>
            <Box flex={1} minW={0}>
              {displayModelName && (
                <Text mb={1} color='text.muted' fontSize='sm' fontWeight={600} textAlign='left'>
                  {displayModelName}
                </Text>
              )}
              {processingStreamingChunkStatus && !data.answer && !current.answerChunks.chunk ? (
                <Flex
                  align='center'
                  gap={3}
                  px={5}
                  py={4}
                  color='text.muted'
                  fontSize='base'
                  bg='bg.surface'
                  borderWidth='1px'
                  borderColor='border.default'
                  borderRadius='14px 14px 14px 4px'
                  shadow='ds_sm'
                >
                  <Spinner color='primary.solid' size='sm' /> {t('common.loading_answer')}
                </Flex>
              ) : (
                <>
                  <Box
                    className='mess_container'
                    px={{ base: 4, md: 6 }}
                    py={4}
                    color='text.default'
                    fontSize='base'
                    lineHeight={1.65}
                    textAlign='left'
                    bg='bg.surface'
                    borderWidth='1px'
                    borderColor='border.default'
                    borderRadius='14px 14px 14px 4px'
                    shadow='ds_sm'
                    whiteSpace='pre-line'
                  >
                    {!data.answer && current.answerChunks.chunk ? <TypingText /> : lastAnswerResult}
                    {hasCitation && (
                      <Box
                        mt={4}
                        pt={4}
                        textAlign='left'
                        borderColor='border.default'
                        borderTopWidth='1px'
                      >
                        <Collapse
                          in={isShowFullCitation}
                          startingHeight={CITATIONS_EXPAND_HEIGHT_OFFSET}
                        >
                          <Box ref={citationsRef}>
                            <Text mb={2} color='text.subtle' fontSize='xs' fontWeight={600}>
                              {t('chat.citations')}
                            </Text>
                            <Flex wrap='wrap' gap={2}>
                              {data?.citations?.map((citation, index) => (
                                <Flex
                                  key={index}
                                  as='button'
                                  align='center'
                                  gap={2}
                                  maxW='240px'
                                  h='30px'
                                  px={3}
                                  color='text.muted'
                                  fontSize='xs'
                                  fontWeight={500}
                                  bg='bg.surface'
                                  borderWidth='1px'
                                  borderColor='border.default'
                                  borderRadius='md'
                                  _hover={{ borderColor: 'primary.solid', color: 'primary.solid' }}
                                  transition='all .14s ease-out'
                                  onClick={() => onOpenFile(citation)}
                                >
                                  <Box
                                    as='span'
                                    color='primary.solid'
                                    fontFamily='mono'
                                    fontWeight={600}
                                  >
                                    {index + 1}
                                  </Box>
                                  <Box as={FileText} flexShrink={0} boxSize='13px' />
                                  <Text
                                    overflow='hidden'
                                    whiteSpace='nowrap'
                                    textOverflow='ellipsis'
                                  >
                                    {citation.file_name}
                                  </Text>
                                </Flex>
                              ))}
                            </Flex>
                          </Box>
                        </Collapse>
                        {isShowViewMoreButton && (
                          <Center mt={2}>
                            <Button
                              fontWeight={500}
                              onClick={onToggleShowFullCitation}
                              rightIcon={
                                isShowFullCitation ? (
                                  <ChevronUp size={16} />
                                ) : (
                                  <ChevronDown size={16} />
                                )
                              }
                              size='ds-sm'
                              variant='ds-ghost'
                            >
                              {isShowFullCitation ? t('common.showLess') : t('common.showMore')}
                            </Button>
                          </Center>
                        )}
                      </Box>
                    )}
                  </Box>
                  {data.answer && (
                    <Flex align='center' gap={2} mt={1}>
                      <Tooltip
                        closeOnClick={false}
                        label={
                          hasCopied
                            ? t('common.copied', { defaultValue: 'Copied' })
                            : t('common.copy', { defaultValue: 'Copy' })
                        }
                      >
                        <IconButton
                          color={hasCopied ? 'state.success' : undefined}
                          aria-label={t('common.copy', { defaultValue: 'Copy' })}
                          icon={hasCopied ? <Check size={15} /> : <Copy size={15} />}
                          onClick={onCopyAnswer}
                          size='ds-sm'
                          variant='ds-ghost'
                        />
                      </Tooltip>
                      <Tooltip label={t('common.exportWord', { defaultValue: 'Export to Word' })}>
                        <IconButton
                          aria-label={t('common.exportWord', { defaultValue: 'Export to Word' })}
                          icon={<FileDown size={15} />}
                          onClick={onExportToWord}
                          size='ds-sm'
                          variant='ds-ghost'
                        />
                      </Tooltip>
                      <Text color='text.subtle' fontSize='xs' textAlign='left'>
                        {timeFormat(data?.updated_at)}
                      </Text>
                    </Flex>
                  )}
                </>
              )}
            </Box>
          </>
        ) : (
          <Flex
            align='center'
            gap={3}
            px={5}
            py={4}
            color='state.error'
            fontSize='base'
            bg='state.errorSoft'
            borderWidth='1px'
            borderColor='state.error'
            borderRadius='14px 14px 14px 4px'
            whiteSpace='pre-line'
          >
            {TEXT_CONSTANT.CONVERSATION.NOT_FOUND_ANSWER}
          </Flex>
        )}
      </Flex>
    </>
  );
};

interface EditableControlsProps {
  isShowEditIcon: boolean;
  value: string;
  cancelRef: React.RefObject<HTMLButtonElement>;
}

function EditableControls({ isShowEditIcon, cancelRef, value }: EditableControlsProps) {
  const { t } = useTranslation();
  const { isEditing, getSubmitButtonProps, getCancelButtonProps, getEditButtonProps } =
    useEditableControls();

  return isEditing ? (
    <ButtonGroup justifyContent='end' size='ds-sm' spacing={2}>
      <Button {...getCancelButtonProps()} ref={cancelRef} size='ds-sm' variant='ds-ghost'>
        {t('common.cancel')}
      </Button>
      <Button {...getSubmitButtonProps()} isDisabled={!value} size='ds-sm' variant='ds-primary'>
        {t('common.update')}
      </Button>
    </ButtonGroup>
  ) : (
    <>
      {isShowEditIcon && (
        <Tooltip label={t('common.editText')}>
          <Flex
            pos='absolute'
            top={2}
            right={3}
            align='center'
            justify='center'
            w='26px'
            h='26px'
            color='primary.on'
            borderRadius='sm'
            _hover={{ cursor: 'pointer', bg: 'whiteAlpha.300' }}
            {...getEditButtonProps()}
          >
            <Pencil size={15} />
          </Flex>
        </Tooltip>
      )}
    </>
  );
}

export default MessageChat;
